// Slice 41 — Revoke modal. Spec §4.2: POST /admin/licenses/:id/revoke
// with `{ reason }`. The reason lands in the audit row (`license.revoke`
// meta.reason) and stays visible in the licenses table's "Revoked reason"
// column when we land it; for now it's audit-only.

import { useEffect, useState } from "react";
import { Api, type LicenseRow } from "../../api/client";
import { HqBadge, HqButton, HqIconButton, Icons } from "../../ui";
import { HqModal } from "../../ui";
import { MonoId } from "./MonoId";

export interface RevokeModalProps {
  target: LicenseRow | null;
  onClose: () => void;
  onRevoked: () => void;
}

export function RevokeModal({ target, onClose, onRevoked }: RevokeModalProps) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Reset whenever the modal closes.
  useEffect(() => {
    if (!target) {
      setReason("");
      setBusy(false);
      setErr(null);
    }
  }, [target]);

  if (!target) return null;

  async function submit() {
    if (!target) return;
    const r = reason.trim();
    if (r.length < 4) {
      setErr("Reason required (min 4 chars) — lands in the audit log.");
      return;
    }
    setBusy(true);
    setErr(null);
    const res = await Api.revokeLicense(target.licenseId, r);
    setBusy(false);
    if (!res.ok) {
      setErr(res.error.message);
      return;
    }
    onRevoked();
  }

  return (
    <HqModal
      open
      onClose={onClose}
      title={
        <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Icons.Revoke width={14} height={14} sw={1.7} style={{ color: "var(--hq-danger)" }} />
          Revoke license
        </span>
      }
      width={460}
      footer={
        <>
          <HqButton variant="ghost" onClick={onClose}>
            Cancel
          </HqButton>
          <HqButton variant="danger" leadingIcon={Icons.Revoke} loading={busy} onClick={submit}>
            Revoke now
          </HqButton>
        </>
      }
    >
      <p
        style={{
          margin: "0 0 14px",
          color: "var(--hq-text-muted)",
          fontSize: 12.5,
          lineHeight: 1.55,
        }}
      >
        Vyvern instances poll <code>/check/&lt;licenseId&gt;</code> every 7 d. Once revoked, this
        license enters the 7-day grace window in the running client (see threat-model §3) and then
        degrades to Free.
      </p>
      <div
        style={{
          padding: 12,
          background: "var(--hq-bg-input)",
          border: "1px solid var(--hq-border-input)",
          borderRadius: "var(--hq-radius-lg)",
          display: "flex",
          flexDirection: "column",
          gap: 8,
          marginBottom: 14,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <MonoId value={target.licenseId} short={24} color="var(--hq-text)" />
          <span style={{ flex: 1 }} />
          <HqBadge tone={target.tier === "pro" ? "pro" : target.tier === "beta" ? "beta" : "free"}>
            {target.tier}
          </HqBadge>
        </div>
        <div
          style={{
            fontSize: 11,
            color: "var(--hq-text-dim)",
            fontFamily: "var(--hq-font-mono)",
          }}
        >
          customer · {target.customerId}
        </div>
      </div>
      <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <span style={{ fontSize: 11.5, color: "var(--hq-text-muted)" }}>Reason (audited)</span>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          placeholder="e.g. customer requested cancellation; chargeback; HWID moved without notice…"
          style={{
            width: "100%",
            background: "var(--hq-bg-input)",
            color: "var(--hq-text)",
            border: "1px solid var(--hq-border-input)",
            borderRadius: "var(--hq-radius-lg)",
            padding: "10px 12px",
            fontSize: 12.5,
            fontFamily: "var(--hq-font-sans)",
            resize: "vertical",
            outline: "none",
          }}
        />
      </label>
      {err && (
        <div
          style={{
            marginTop: 10,
            padding: "8px 12px",
            borderRadius: "var(--hq-radius-md)",
            background: "var(--hq-danger-soft)",
            color: "var(--hq-danger)",
            fontSize: 12,
            border: "1px solid rgba(251,113,133,.25)",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <HqIconButton icon={Icons.X} size="sm" title="Dismiss" onClick={() => setErr(null)} />
          {err}
        </div>
      )}
    </HqModal>
  );
}
